import { useCallback, useMemo, type FC } from "react";
import { useListProjects, type PlainProject } from "@/queries/project-queries";
import { getRepoUrlFromInitializer } from "@/queries/environment-queries";
import { Combobox } from "@/components/flexkit/combobox/Combobox";

type TriggerProjectSelectorProps = {
    selectedProjectIds: string[];
    onSelectionChange: (projectIds: string[]) => void;
    disabled?: boolean;
};

export const TriggerProjectSelector: FC<TriggerProjectSelectorProps> = ({
    selectedProjectIds,
    onSelectionChange,
    disabled = false,
}) => {
    const { data: projectsData, isPending: isLoadingProjects } = useListProjects({ enabled: true });

    const availableProjects = useMemo(() => {
        if (!projectsData?.projects) return [];
        const available: (PlainProject & { repoUrl: string })[] = [];
        for (const project of projectsData.projects) {
            if (selectedProjectIds.includes(project.id)) continue;
            available.push({
                ...project,
                repoUrl: getRepoUrlFromInitializer(project.initializer) ?? "",
            });
        }
        return available;
    }, [projectsData?.projects, selectedProjectIds]);

    const handleAddProject = useCallback(
        (projectId: string) => {
            if (!selectedProjectIds.includes(projectId)) {
                onSelectionChange([...selectedProjectIds, projectId]);
            }
        },
        [selectedProjectIds, onSelectionChange],
    );

    if (disabled) {
        return null;
    }

    let comboboxContent: React.ReactNode;
    if (isLoadingProjects) {
        comboboxContent = <Combobox.Loading>Loading projects…</Combobox.Loading>;
    } else if (availableProjects.length > 0) {
        comboboxContent = (
            <Combobox.List
                items={availableProjects}
                searchKeys={["metadata.name", "repoUrl"]}
                noMatchesComponent={<Combobox.Empty>No matching projects.</Combobox.Empty>}
            >
                {(project) => (
                    <Combobox.ListItem key={project.id} value={project.id}>
                        <Combobox.ListItemTitle className="text-base">{project.metadata?.name}</Combobox.ListItemTitle>
                        <Combobox.ListItemDescription className="text-xs text-content-muted">
                            {project.initializer ? getRepoUrlFromInitializer(project.initializer) : "-"}
                        </Combobox.ListItemDescription>
                    </Combobox.ListItem>
                )}
            </Combobox.List>
        );
    } else if (!projectsData?.projects || projectsData.projects.length === 0) {
        comboboxContent = <Combobox.Empty>No projects found.</Combobox.Empty>;
    } else {
        comboboxContent = <Combobox.Empty>All projects have been selected.</Combobox.Empty>;
    }

    return (
        <Combobox
            // the value here is always empty because the selected projects are not displayed in the combobox
            // but we still want Combobox to work as a controlled component in order to receive the selected value from the user when they pick a project from the combobox
            value=""
            onValueChange={handleAddProject}
            disabled={disabled}
            loading={isLoadingProjects}
            className="w-fit"
            filterPlaceholder="Search projects…"
        >
            <Combobox.Value>
                <Combobox.ValueLabel>Add a project…</Combobox.ValueLabel>
            </Combobox.Value>
            <Combobox.Popover className="w-128 max-w-[calc(100vw-1rem)]">{comboboxContent}</Combobox.Popover>
        </Combobox>
    );
};
